import {hasPageMessage} from '../../lib/i18n';
import {tx, ptx, usePageText} from '../../lib/page-text';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { guides, type Guide } from './guides';
import './live-help.css';

export function LiveHelp({ topic = 'create', label = 'Help & guides', audience = 'host', compact = false }: {
  topic?: string; label?: string; audience?: 'host' | 'guest'; compact?: boolean;
}) {
  const {locale, locales} = usePageText();
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  return <>
    <button ref={trigger} type="button" className={`live-help-trigger${compact ? ' is-compact' : ''}`} aria-haspopup="dialog"
      aria-label={tx(label)} title={compact ? tx(label) : undefined}
      onClick={() => setOpen(true)} lang={locale}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M9.5 9a2.5 2.5 0 1 1 4 2c-1 .6-1.5 1.1-1.5 2.5M12 16v1"/></svg>
      <span>{tx(label)}</span>
    </button>
    {open && createPortal(<HelpDialog topic={topic} audience={audience} restoreFocusTo={trigger.current}
      onClose={() => setOpen(false)}/>, document.body)}
  </>;
}

function HelpDialog({ topic, audience, onClose, restoreFocusTo }: { topic: string; audience: 'host' | 'guest'; onClose: () => void; restoreFocusTo: HTMLElement | null }) {
  const {locale, locales} = usePageText();
  const complete = (g: Guide) => [g.title, g.summary, g.note, ...g.steps].every(hasPageMessage);
  const guideText = (g: Guide, source: string) => complete(g) ? tx(source) : source;
  const available = guides.filter(g => audience === 'host' || g.guest);
  const [selected, setSelected] = useState(available.find(g => g.id === topic) ?? available[0]);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('All topics');
  const [selection, setSelection] = useState(0);
  const dialog = useRef<HTMLDialogElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const titleId = useId(), searchId = useId();
  const terms = query.trim().toLocaleLowerCase(locale).split(/\s+/).filter(Boolean);
  const matches = available.filter(g => (category === 'All topics' || g.category === category)
    && terms.every(term => [g.title, g.summary, g.note, ...g.steps].map(s => tx(s)).join(' ').toLocaleLowerCase(locale).includes(term)));
  useEffect(() => {
    const previous = restoreFocusTo ?? document.activeElement as HTMLElement | null;
    const el = dialog.current; el?.showModal();
    return () => { el?.close(); if (previous?.isConnected) previous.focus(); };
  }, []);
  useLayoutEffect(() => {
    if (!selection) return;
    heading.current?.focus(); heading.current?.scrollIntoView({ block: 'nearest' });
  }, [selection]);
  function select(guide: Guide) {
    setSelected(guide);
    // Move focus with this render, without a delayed callback stealing the next focus.
    setSelection(value => value + 1);
  }
  function download() {
    const downloadLocale = available.every(complete) ? locale : 'en';
    const tr = downloadLocale === 'en' ? (source: string) => source : tx;
    const text = [tr('Kardolive user guide') + ' — ' + locales[downloadLocale].name, ...available.map(g =>
      `${tr(g.title)}\n${tr(g.summary)}\n\n${g.steps.map((s, i) => `${new Intl.NumberFormat(downloadLocale).format(i + 1)}. ${tr(s)}`).join('\n')}\n\n${tr(g.note)}`)].join('\n\n————————\n\n');
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
    const link = document.createElement('a'); link.href = url; link.download = `kardolive-${audience}-guide-${downloadLocale}.txt`;
    document.body.append(link); link.click(); link.remove(); setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  return <dialog ref={dialog} className="live-help-dialog" aria-labelledby={titleId} lang={locale} dir={locales[locale].dir}
    onCancel={e => { e.preventDefault(); onClose(); }}
    onKeyDown={e => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); return; }
      if (e.key !== 'Tab') return;
      const items = Array.from(dialog.current?.querySelectorAll<HTMLElement>('button, input, select, a[href]') ?? [])
        .filter(el => !el.matches(':disabled') && el.getClientRects().length > 0);
      if (!items.length) return;
      const index = items.indexOf(document.activeElement as HTMLElement);
      if (index === -1 || (!e.shiftKey && index === items.length - 1) || (e.shiftKey && index === 0)) {
        e.preventDefault(); items[e.shiftKey ? items.length - 1 : 0].focus();
      }
    }}>
    <header className="live-help-header">
      <div><p>{ptx("Learn Kardolive · {v0}", {v0: (locales[locale].name)})}</p><h2 id={titleId}>{tx("Help & guides")}</h2></div>
      <button type="button" onClick={onClose} aria-label={tx("Close help")}>×</button>
    </header>
    <div className="live-help-body">
      <aside className="live-help-index" aria-label={tx("Find a guide")}>
        <label htmlFor={searchId}>{tx("Search guides")}</label>
        <input id={searchId} type="search" value={query} placeholder={tx("Try screen, course, stop…")} onChange={e => setQuery(e.target.value)}/>
        <label htmlFor={`${searchId}-topic`}>{tx("Topic")}</label><select id={`${searchId}-topic`} value={category} onChange={e => setCategory(e.target.value)}>
          {['All topics', ...new Set(available.map(g => g.category))].map(c => <option key={c} value={c}>{tx(c)}</option>)}
        </select>
        <p className="live-help-count" role="status">{tx('Guides: {count}', {count: matches.length})}</p>
        {!matches.length && <div className="live-help-empty"><p>{tx("No guides match. Try a different word or topic.")}</p><button type="button" onClick={() => { setQuery(''); setCategory('All topics'); }}>{tx("Clear filters")}</button></div>}
        <nav aria-label={tx("Guide topics")}>{matches.map(g => <button key={g.id} type="button" aria-current={g.id === selected.id ? 'true' : undefined} onClick={() => select(g)}><span>{tx(g.category)}</span>{tx(g.title)}</button>)}</nav>
      </aside>
      <article className="live-help-article" lang={complete(selected) ? locale : 'en'} dir={complete(selected) ? locales[locale].dir : 'ltr'} aria-labelledby={`${titleId}-article`}>
        <p className="live-help-category">{guideText(selected, selected.category)}</p>
        <h3 ref={heading} id={`${titleId}-article`} tabIndex={-1}>{guideText(selected, selected.title)}</h3>
        <p className="live-help-summary">{guideText(selected, selected.summary)}</p>
        <ol>{selected.steps.map(step => <li key={step}>{guideText(selected, step)}</li>)}</ol>
        <div className="live-help-note"><strong>{tx("Good to know")}</strong><p>{guideText(selected, selected.note)}</p></div>
        <h4>{tx("Related guides")}</h4><div className="live-help-related">{selected.related.map(id => available.find(g => g.id === id)).filter((g): g is Guide => !!g).map(g => <button type="button" key={g.id} onClick={() => select(g)}>{tx(g.title)}<span aria-hidden="true"> →</span></button>)}</div>
      </article>
    </div>
    <footer className="live-help-footer"><span>{available.every(complete) ? tx("Keep these steps for your team.") : <span>{tx('Guide instructions remain in English until a complete translation is available.')}</span>}</span><button type="button" onClick={download}>{tx(audience === 'guest' ? 'Download speaker guide (.txt)' : 'Download user guide (.txt)')}</button></footer>
  </dialog>;
}
